import _ from 'lodash';

import { ActionStepContentType, ActionStepType } from 'constants/aiChat';
import { jsonParse, jsonStringify } from 'utils/json';

import { CONDITION_TOOL_NAMES } from './StepPendingToolCall/StepPendingToolCall.constants';
import { extractConditions } from './StepPendingToolCall/StepPendingToolCall.utils';

const ASSISTANT_ROLE = 'assistant';

// LEN-36: the confirmation-disposition badge is only meaningful on a WRITE tool
// call — a read that ran automatically is unremarkable. The condition tools
// (add_conditions_from_library / add_adhoc_conditions) carry no actionStepType
// (their `add_` prefix isn't in the type map), so they're matched by name; the
// other genuine mutations are matched by their MODIFYING_RECORD / EXECUTING_COMMAND
// type. Everything else (reads, guideline lookups, finish_step) is left unbadged.
export const shouldBadgeToolDisposition = (toolName, actionStepType) =>
  CONDITION_TOOL_NAMES.has(toolName) ||
  actionStepType === ActionStepType.MODIFYING_RECORD ||
  actionStepType === ActionStepType.EXECUTING_COMMAND;

// The persisted step messages carry their payload as a raw JSON string in
// `content` (there are no structured content blocks server-side). A tool-call
// message is a JSON envelope of the shape:
//   { "tool": "get_loan_application", "isError": null, "content": [{ "type": "text", "text": "…" }] }
// while a narrative / finish-step message is plain text. This parses the
// envelope FE-side so the friendly "Tool calls" view can render each call like
// the AI Chat panel and surface the step's final narrative output.
const tryParseEnvelope = content => {
  if (!_.isString(content)) {
    return null;
  }

  try {
    const parsed = jsonParse(content);

    return _.isPlainObject(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const formatToolResult = envelope => {
  if (Array.isArray(envelope.content)) {
    return envelope.content.map(block => block?.text ?? '').join('\n');
  }

  if (_.isString(envelope.content)) {
    return envelope.content;
  }

  return jsonStringify(envelope.content ?? '', null, 2);
};

// DEV-3052: a single assistant message can carry MULTIPLE tool envelopes
// concatenated together (+ a trailing prose summary) — the dynamic-orchestrator
// (DEV-3051) synthesis turn sometimes echoes prior tool envelopes verbatim into
// one message. `JSON.parse` of the whole string then throws, so the single-
// envelope fast path misses it. This splits the content into ordered segments
// of balanced top-level `{…}` objects vs. plain text, quote/escape-aware so
// braces inside JSON strings don't confuse the scan.
const SCAN_INITIAL = {
  segments: [],
  depth: 0,
  start: -1,
  inString: false,
  escaped: false,
  lastEnd: 0,
};

// Advances the scan one character while inside a JSON string literal, honouring
// backslash escapes so an escaped quote does not end the string early.
const scanStringChar = (state, ch) => {
  if (state.escaped) {
    return { ...state, escaped: false };
  }

  if (ch === '\\') {
    return { ...state, escaped: true };
  }

  if (ch === '"') {
    return { ...state, inString: false };
  }

  return state;
};

// Advances the scan one character while outside any string, tracking brace
// depth so each top-level `{…}` object is captured as a `json` segment and the
// text between objects as `text` segments.
const scanChar = (content, state, ch, i) => {
  if (state.inString) {
    return scanStringChar(state, ch);
  }

  if (ch === '"') {
    return { ...state, inString: true };
  }

  if (ch === '{') {
    const openedTopLevel = state.depth === 0;
    const segments =
      openedTopLevel && i > state.lastEnd
        ? [...state.segments, { kind: 'text', value: content.slice(state.lastEnd, i) }]
        : state.segments;

    return {
      ...state,
      segments,
      depth: state.depth + 1,
      start: openedTopLevel ? i : state.start,
    };
  }

  if (ch === '}' && state.depth > 0) {
    const closedTopLevel = state.depth === 1 && state.start !== -1;

    return {
      ...state,
      segments: closedTopLevel
        ? [...state.segments, { kind: 'json', value: content.slice(state.start, i + 1) }]
        : state.segments,
      depth: state.depth - 1,
      start: closedTopLevel ? -1 : state.start,
      lastEnd: closedTopLevel ? i + 1 : state.lastEnd,
    };
  }

  return state;
};

const segmentContent = content => {
  const finalState = [...Array(content.length).keys()].reduce(
    (state, i) => scanChar(content, state, content[i], i),
    SCAN_INITIAL,
  );

  if (finalState.lastEnd < content.length) {
    return [...finalState.segments, { kind: 'text', value: content.slice(finalState.lastEnd) }];
  }

  return finalState.segments;
};

// Parses a (possibly) concatenated content string into the tool envelopes it
// contains plus the remaining narrative prose. Non-tool JSON and plain text are
// preserved as prose so a summary that merely mentions braces is never eaten.
const parseConcatenatedEnvelopes = content => {
  if (!_.isString(content)) {
    return { toolEnvelopes: [], prose: '' };
  }

  const toolEnvelopes = [];
  const proseParts = [];

  segmentContent(content).forEach(segment => {
    if (segment.kind === 'json') {
      try {
        const parsed = jsonParse(segment.value);

        if (_.isPlainObject(parsed) && _.isString(parsed.tool)) {
          toolEnvelopes.push(parsed);

          return;
        }
      } catch {
        // Not valid JSON after all -> fall through and keep it as prose.
      }
    }

    proseParts.push(segment.value);
  });

  return { toolEnvelopes, prose: proseParts.join('').trim() };
};

// A step's final synthesis is stored twice: the raw transcript (/messages) keeps
// the model's original [[CITE:...]] markers, while the backend-cleaned
// OutputResult blob rewrites them to the [^cite:<slug>] form the citation cards
// resolve (DEV-3591). The narrative resolver below prefers the cleaned blob and,
// while it is still loading, strips the raw markers so a literal [[CITE:...]] tag
// never reaches the screen. Non-global so the shared test regex carries no
// lastIndex state across calls.
const RAW_CITATION_MARKER_TEST_REGEX = /\[\[CITE:[^\]]+\]\]/i;
const RAW_CITATION_MARKER_STRIP_REGEX = /\[\[CITE:[^\]]+\]\]/gi;

export const hasRawCitationMarkers = text =>
  _.isString(text) && RAW_CITATION_MARKER_TEST_REGEX.test(text);

const stripRawCitationMarkers = text => {
  if (!_.isString(text)) {
    return text;
  }

  return text
    .replace(RAW_CITATION_MARKER_STRIP_REGEX, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

// The cleaned OutputResult blob is a plain string for a synthesis step and a
// structured stub object when the step produced no synthesis; only the string
// form carries the [^cite:<slug>] markers, so fall back to the raw prose (markers
// stripped) whenever the blob is not a resolved string.
export const resolveFinalOutputNarrative = (finalOutput, cleanedOutputBlob) => {
  if (!hasRawCitationMarkers(finalOutput)) {
    return finalOutput;
  }

  return _.isString(cleanedOutputBlob) ? cleanedOutputBlob : stripRawCitationMarkers(finalOutput);
};

export const parseStepToolActivity = messages => {
  const result = { toolCalls: [], finalOutput: null };

  if (!Array.isArray(messages)) {
    return result;
  }

  messages.forEach(message => {
    const role = (message?.role ?? ASSISTANT_ROLE).toLowerCase();

    if (role !== ASSISTANT_ROLE) {
      return;
    }

    // Fast path: the whole content is one clean tool envelope.
    const envelope = tryParseEnvelope(message?.content);

    if (!_.isNil(envelope) && _.isString(envelope.tool)) {
      result.toolCalls.push({
        id: message.id,
        toolName: envelope.tool,
        isError: !!envelope.isError,
        // LEN-36: how a write tool ran — 'auto' (no confirmation) / 'confirmed'
        // (human-approved) / null (a read, unbadged).
        confirmationDisposition: envelope.confirmationDisposition ?? null,
        result: formatToolResult(envelope),
      });

      return;
    }

    // DEV-3052: the content may hold several concatenated tool envelopes
    // (+ trailing summary). Split so each renders as its own row and the
    // leftover prose becomes the step's narrative output.
    const { toolEnvelopes, prose } = parseConcatenatedEnvelopes(message?.content);

    if (toolEnvelopes.length > 0) {
      toolEnvelopes.forEach((toolEnvelope, index) => {
        result.toolCalls.push({
          id: `${message.id}-${index}`,
          toolName: toolEnvelope.tool,
          isError: !!toolEnvelope.isError,
          confirmationDisposition: toolEnvelope.confirmationDisposition ?? null,
          result: formatToolResult(toolEnvelope),
        });
      });

      if (prose.length > 0) {
        result.finalOutput = prose;
      }

      return;
    }

    // Plain-text assistant turn -> narrative. The finish-step summary is the
    // last non-empty narrative, so later ones overwrite earlier ones.
    const narrative = _.isString(message?.content) ? message.content.trim() : '';

    if (narrative.length > 0) {
      result.finalOutput = message.content;
    }
  });

  return result;
};

// A condition-writing tool result is a JSON object, optionally prefixed with a
// prose summary. Slice the object out and parse it once. It comes in two shapes:
//   - executed:  Placed 3 library condition(s). {"placed":[{…}],"rejected":[]}
//   - ad-hoc:    {"conditions":[{"internalText":"…","borrowerText":"…","priorToType":"Doc"}]}
// (the run records the proposed ad-hoc args verbatim as the result). Rendering
// that raw string dumps JSON at the user — the callers below build a readable
// summary + bulleted list from whichever shape parses.
const parseConditionResultObject = result => {
  if (!_.isString(result)) {
    return null;
  }

  const start = result.indexOf('{');
  const end = result.lastIndexOf('}');

  if (start === -1 || end <= start) {
    return null;
  }

  try {
    const parsed = jsonParse(result.slice(start, end + 1));

    return _.isPlainObject(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

// LEN-35: maps the proposed ad-hoc `conditions:[…]` onto the same {id,text}
// bullet list the placed/library result uses, so an ad-hoc call reads as a
// condition list instead of dumping raw JSON. Uses the internal condition text
// (falling back to the borrower-facing text), mirroring the library card's
// bullets. Reuses extractConditions so the arg-shape tolerance stays in one place.
const toAdHocConditionItems = (parsed, idPrefix) =>
  extractConditions(parsed)
    .map((condition, index) => {
      const text = condition.internalText ?? condition.borrowerText ?? '';

      return { id: `${idPrefix}-condition-${index}`, text: _.isString(text) ? text.trim() : '' };
    })
    .filter(item => item.text.length > 0);

const toConditionListItems = (entries, idPrefix) =>
  (Array.isArray(entries) ? entries : [])
    .map((entry, index) => {
      const text = _.isString(entry) ? entry : (entry?.message ?? entry?.reason ?? '');

      return { id: `${idPrefix}-${index}`, text: _.isString(text) ? text.trim() : '' };
    })
    .filter(item => item.text.length > 0);

const textBlock = (id, text) => ({
  id,
  contentType: ActionStepContentType.TEXT,
  contentProps: { text },
});

const listBlock = (id, items) => ({
  id,
  contentType: ActionStepContentType.LIST,
  contentProps: { items },
});

// Builds the ActionStep content blocks for one condition tool result: a summary
// line + the placed/rejected lists. Extracted so buildToolCallContent stays a
// thin shape-router.
const buildPlacedRejectedContent = (id, conditionResult) => {
  const placedItems = toConditionListItems(conditionResult.placed, `${id}-placed`);
  const rejectedItems = toConditionListItems(conditionResult.rejected, `${id}-rejected`);

  // Count from the raw envelope, not the rendered items, so the summary reflects
  // what the tool actually placed even if a condition carried no message text.
  const placedCount = Array.isArray(conditionResult.placed) ? conditionResult.placed.length : 0;
  const rejectedCount = Array.isArray(conditionResult.rejected)
    ? conditionResult.rejected.length
    : 0;

  const summary = [`Placed ${placedCount} condition${placedCount === 1 ? '' : 's'}.`];

  if (rejectedCount > 0) {
    summary.push(`Rejected ${rejectedCount}.`);
  }

  return [
    textBlock(`${id}-summary`, summary.join(' ')),
    ...(placedItems.length > 0 ? [listBlock(`${id}-placed`, placedItems)] : []),
    ...(rejectedItems.length > 0
      ? [textBlock(`${id}-rejected-label`, 'Rejected:'), listBlock(`${id}-rejected`, rejectedItems)]
      : []),
  ];
};

// Builds the ActionStep content blocks for one tool call. Condition-writing
// tools get a readable summary + bulleted list — from a placed/rejected result
// (library conditions) or the proposed `conditions:[…]` args the ad-hoc tool
// records as its result (LEN-35); every other tool, and any condition result
// that doesn't parse, keeps the single raw-text block.
export const buildToolCallContent = ({ id, toolName, result }) => {
  if (!CONDITION_TOOL_NAMES.has(toolName)) {
    return [textBlock(`${id}-result`, result)];
  }

  const parsed = parseConditionResultObject(result);

  if (parsed && (Array.isArray(parsed.placed) || Array.isArray(parsed.rejected))) {
    return buildPlacedRejectedContent(id, parsed);
  }

  const adHocItems = parsed ? toAdHocConditionItems(parsed, id) : [];

  if (adHocItems.length > 0) {
    const count = Array.isArray(parsed.conditions) ? parsed.conditions.length : adHocItems.length;
    const summary = `Placed ${count} condition${count === 1 ? '' : 's'}.`;

    return [textBlock(`${id}-summary`, summary), listBlock(`${id}-conditions`, adHocItems)];
  }

  return [textBlock(`${id}-result`, result)];
};
